import {
  ALLOWED_IMAGE_EXTENSIONS,
  ALLOWED_IMAGE_MIME_TYPES,
  MAX_IMAGE_UPLOAD_SIZE,
} from "../settings/shared/constants";

const normalizeMime = (mime) => {
  const t = (mime || "").toLowerCase();
  // Some browsers may report jpg as image/jpg
  if (t === "image/jpg") return "image/jpeg";
  return t;
};

const getExtension = (name) => {
  if (!name || typeof name !== "string") return "";
  const parts = name.split(".");
  if (parts.length < 2) return "";
  return parts.pop().toLowerCase();
};

export const formatMB = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
};

/**
 * Validates file size and type using mime/extension allowlists.
 *
 * Rules:
 * - If mime is present, it must be allowed (when allowlist provided)
 * - If mime is missing, extension allowlist is used (when provided)
 * - If both mime and extension are present and allowlists exist, both must match
 */
export const validateFile = (
  file,
  {
    maxSizeBytes,
    allowedMimeTypes,
    allowedExtensions,
    label = "file",
    allowedLabel,
  } = {}
) => {
  if (!file) return { ok: false, error: `Please select a valid ${label}.` };

  const size = Number(file.size || 0);
  const mime = normalizeMime(file.type);
  const ext = getExtension(file.name);

  if (Number.isFinite(maxSizeBytes) && maxSizeBytes > 0 && size > maxSizeBytes) {
    return {
      ok: false,
      error: `Please upload a ${label} of ${formatMB(maxSizeBytes)} or less.`,
    };
  }

  const hasMime = !!mime;
  const mimeAllowlist = Array.isArray(allowedMimeTypes) ? allowedMimeTypes : null;
  const extAllowlist = Array.isArray(allowedExtensions) ? allowedExtensions : null;

  const mimeOk = !mimeAllowlist || (hasMime && mimeAllowlist.includes(mime));
  const extOk = !extAllowlist || (!!ext && extAllowlist.includes(ext));

  // If we have a mime and a mime allowlist, mime must match.
  if (mimeAllowlist && hasMime && !mimeOk) {
    return {
      ok: false,
      error: `Please upload a valid ${allowedLabel || label}.`,
    };
  }

  // If mime missing but extension allowlist exists, require extension match.
  if (mimeAllowlist && !hasMime && extAllowlist && !extOk) {
    return {
      ok: false,
      error: `Please upload a valid ${allowedLabel || label}.`,
    };
  }

  // If both allowlists exist and we have both mime and ext, require both.
  if (mimeAllowlist && extAllowlist && hasMime && ext && (!mimeOk || !extOk)) {
    return {
      ok: false,
      error: `Please upload a valid ${allowedLabel || label}.`,
    };
  }

  return { ok: true, error: null };
};

export const validateImageFile = (
  file,
  {
    maxSizeBytes = MAX_IMAGE_UPLOAD_SIZE,
    allowedMimeTypes = ALLOWED_IMAGE_MIME_TYPES,
    allowedExtensions = ALLOWED_IMAGE_EXTENSIONS,
    allowedLabel = "image file (JPG, PNG, GIF, WebP, SVG)",
  } = {}
) =>
  validateFile(file, {
    label: "image file",
    maxSizeBytes,
    allowedMimeTypes,
    allowedExtensions,
    allowedLabel,
  });


